import React, { useState, useEffect } from "react";
import { FaWhatsapp } from "react-icons/fa";
import { SendHorizontal } from "lucide-react";
import MphLogo from '../assets/MphLogo.png';
import RecruiterLogo from '../assets/RecruiterLogo.png';
import { FiInfo } from "react-icons/fi";
import CustomDropdown from "./dropdown";
import { RecruiterApi } from "../supports/recruiterApi";
import { resumeUpload as ResumeUpload } from "../supports/resumeUpload";
import { ACTIVE_STATUSES, generateRequestId } from "./common";

const extractTemplateParts = (message, fallbackJobText) => {
  if (!message || typeof message !== "string") {
    return {
      prefix: "Hi,\n\nThis is MPH. We have an exciting opportunity for ",
      jobText: fallbackJobText,
      suffix: " that aligns with your experience.\nBest regards,\nMPH Team",
    };
  }

  const pattern =
    /(We have an exciting opportunity for\s+)(.*?)(\s+that aligns well with your experience\.|\s+that aligns with your experience\.)/i;
  const match = message.match(pattern);

  if (!match) {
    return {
      prefix: message,
      jobText: "",
      suffix: "",
    };
  }

  const fullMatch = match[0];
  const matchStart = message.indexOf(fullMatch);
  const matchEnd = matchStart + fullMatch.length;

  return {
    prefix: message.slice(0, matchStart) + match[1],
    jobText: (match[2] || "").trim() || fallbackJobText,
    suffix: match[3] + message.slice(matchEnd),
  };
};

const WhatsAppModal = ({
  isOpen,
  onClose,
  onSend,
  candidateName,
  candidatePhone,
  recruiterName,
  recruiterId,
  jobDetails,
  templateMessage,
  sending,
  notifications = [],
  candidateId,
}) => {
  const [jobValue, setJobValue] = useState(jobDetails?.title?.trim() ? jobDetails.title : "");
  const [messagePrefix, setMessagePrefix] = useState("");
  const [messageSuffix, setMessageSuffix] = useState("");
  const [showCompose, setShowCompose] = useState(false);
  const [jobOptions, setJobOptions] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [fetchedConversations, setFetchedConversations] = useState(notifications);
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [jobSelectionError, setJobSelectionError] = useState("");

  const defaultMessage = templateMessage || `Hi ${candidateName || "Candidate"},
This is ${recruiterName || "the recruiter"} from MPH. We have an exciting opportunity for a ${jobDetails?.title || 'Job - JOXXXXXX'} that aligns well with your experience. Would you like to receive a call from us to discuss more about this opportunity.
Best regards,
MPH Team`;

  const toTitleCase = (str) => {
  return str
    .toLowerCase()
    .split(" ")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = date.toLocaleString("en-US", { month: "short" });
    const year = date.getFullYear();
    const time = date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
    return `${day} ${month} ${year}, ${time}`;
  };

  const hasSent = fetchedConversations.length > 0;
  const alreadySent = hasSent && !showCompose;
  const hasExistingJobTitle = Boolean(jobDetails?.title?.trim());
  const jobSelectionRequired = !hasExistingJobTitle;

  useEffect(() => {
    if (isOpen) setJobSelectionError("");
  }, [isOpen]);

  useEffect(() => {
    const fallbackJobText = jobDetails?.title || "Job - JOXXXXXX";
    const { prefix, jobText, suffix } = extractTemplateParts(defaultMessage, fallbackJobText);
    setMessagePrefix(prefix);
    setJobValue(jobText);
    setMessageSuffix(suffix);
  }, [defaultMessage]);

  // Fetch conversations from API when modal opens (if candidateId is provided)
  useEffect(() => {
    if (!isOpen || !candidateId) {
      setFetchedConversations(notifications);
      return;
    }
    setConversationsLoading(true);
    RecruiterApi.getCandidateWhatsAppConversations(candidateId)
      .then((res) => {
        const convs = Array.isArray(res?.conversations) ? res.conversations : [];
        setFetchedConversations(convs.map((c) => ({
          message_status: c.message_status,
          is_responded: c.is_responded,
          sent_at: c.sent_at,
          response: c.response,
          sent_by: c.sent_by,
          mph_jobid: c.mph_jobid,
          job_id: c.job_id,
          job_title: c.job_title,
        })));
      })
      .catch(() => setFetchedConversations(notifications))
      .finally(() => setConversationsLoading(false));
  }, [isOpen, candidateId]);

  // Fetch all jobs when compose view is shown and no job is pre-selected
  useEffect(() => {
    if (!isOpen || hasExistingJobTitle) return;
    ResumeUpload.fetchAlljobsDropdown()
      .then((res) => {
        const jobs = Array.isArray(res?.data) ? res.data : [];
        setJobOptions(
          jobs.map((j) => ({
            id: j.id,
            mph_jobid: j.mph_jobid,
            job_title: j.job_title || "",
          }))
        );
      })
      .catch(() => {});
  }, [isOpen, hasExistingJobTitle]);

  const handleJobSelect = (selectedId) => {
    const job = jobOptions.find((j) => j.id === selectedId);
    setSelectedJob(job || null);
    setJobValue(job?.job_title || "");
    setJobSelectionError("");
  };

  const handleSend = () => {
    if (jobSelectionRequired && !selectedJob) {
      setJobSelectionError("Please select a job to send this WhatsApp message.");
      return;
    }
    if (!jobValue?.trim()) {
      setJobSelectionError("Job title is required to send this WhatsApp message.");
      return;
    }
    onSend(jobValue?.trim() || jobDetails?.title || "Job - JOXXXXXX", selectedJob);
  };

  if (!isOpen) return null;

  const composedMessage = `${messagePrefix}${jobValue || ""}${messageSuffix}`;
  const sendLooksDisabled = sending || !jobValue?.trim() || (jobSelectionRequired && !selectedJob);
  const sendDisabled = sending;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30 anim-slideDown"
      onClick={onClose}
    >
      <div
        className="bg-white max-h-[80vh] rounded-xl shadow-lg relative flex flex-col w-[600px]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Sticky Header */}
        <div className="bg-white sticky top-0 z-10 p-4 pb-2 border-b rounded-t-xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {alreadySent && (
                <div className="flex items-center gap-4">
                  <img src={MphLogo} alt="MPH Company Logo" className="h-5" />
                  {/* <img src={RecruiterLogo} alt="Recruiter Logo" className="w-8 mb-1" /> */}
                </div>
              )}
            </div>
            <button
              className="text-gray-66 hover:text-gray-333 bg-gray-100 rounded-full p-1"
              onClick={onClose}
              type="button"
              aria-label="Close WhatsApp modal"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path
                  d="M6 6L14 14M14 6L6 14"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>
          {!alreadySent && (
            <div className="flex items-center gap-2 mt-2">
              <FaWhatsapp className="text-green-600" size={16} />
              <p className="text-xs text-gray-666">
                <span className="font-semibold">WhatsApp Message</span>
              </p>
            </div>
          )}
        </div>

        {/* Scrollable Content */}
        <div className="overflow-y-auto p-4 flex-1">
          {alreadySent && (
            <div className="py-2">
              <p className="text-sm  text-gray-700 mb-3">Conversation History</p>
              <div className="flex flex-col gap-2 mb-4">
                {conversationsLoading ? (
                  <p className="text-xs text-gray-400 py-2">Loading history...</p>
                ) : null}
                {!conversationsLoading && fetchedConversations.map((n, i) => {
                  const isPending = !n.is_responded;
                  const isYes = n.response === "Yes";
                  const isNo = n.response === "No";
                  return (
                    <div key={i} className="flex items-start justify-between border border-gray-200 rounded-lg px-4 py-3 gap-4">
                      <div className="flex flex-col gap-0.5 min-w-0">
                        <div className="flex items-baseline gap-1.5 min-w-0">
                          <span className="text-sm text-gray-800 font-semibold truncate">{toTitleCase(n.job_title) || "—"}</span>
                          {n.mph_jobid && (
                            <span className="text-sm text-gray-800 shrink-0">&nbsp;-&nbsp;{n.mph_jobid}</span>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                          {n.sent_by ? (
                            <span className="text-xs text-gray-500">Sent by {n.sent_by}</span>
                          ) : null}
                          <span className="text-xs text-gray-400">{n.sent_at ? `on ${formatDateTime(n.sent_at)}` : "—"}</span>
                        </div>
                      </div>
                      <div className="shrink-0">
                        {isPending && (
                          <span className="text-xs font-semibold text-yellow-600 bg-yellow-50 border border-yellow-200 rounded-full px-3 py-0.5 whitespace-nowrap">Awaiting response</span>
                        )}
                        {isYes && (
                          <span className="text-xs font-semibold text-[rgb(56 76 63)] bg-green-50 border border-green-200 rounded-full px-3 py-0.5 whitespace-nowrap">Interested</span>
                        )}
                        {isNo && (
                          <span className="text-xs font-semibold text-red-600 bg-red-50 border border-red-200 rounded-full px-3 py-0.5 whitespace-nowrap">Not interested</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center justify-end">
                <button
                  onClick={() => setShowCompose(true)}
                  className="px-4 py-2 text-sm rounded-full text-white font-semibold profiler-bg hover:opacity-90 flex items-center gap-2"
                >
                  Send again
                </button>
              </div>
            </div>
          )}
        </div>

        {!alreadySent && (
          <>
            <div className="overflow-y-auto p-4 flex-1 min-h-[12rem]">
              {!hasExistingJobTitle && (
                <div className="mb-3">
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Job Title / Reference
                  </label>
                  <CustomDropdown
                    options={jobOptions}
                    value={selectedJob?.id ?? null}
                    onChange={handleJobSelect}
                    placeholder="Select a job..."
                    optionValue="id"
                    isJobList={true}
                    searchable={true}
                  />
                  {jobSelectionError ? (
                    <p className="text-xs text-red-600 mt-1">{jobSelectionError}</p>
                  ) : null}
                  <div className="flex items-start text-[10px] text-blue-500 m-2">
                    <FiInfo className="mr-1 mt-[2px]" size={12} />
                    <p>Select the job opportunity you want to notify the candidate about.</p>
                  </div>
                </div>
              )}
              <div className="bg-gray-50 rounded p-4 text-sm text-gray-700">
                <div className="whitespace-pre-line">
                  {composedMessage}
                </div>
              </div>

              {/* Info Notice */}
              <div className="bg-blue-50 border border-blue-200 rounded p-3 mt-4 flex gap-2">
                <svg
                  className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>
                <p className="text-xs text-blue-800">
                  This message will be sent via WhatsApp to the candidate's registered phone number.
                </p>
              </div>
            </div>

            {/* Sticky Footer */}
            <div className="sticky bottom-0 bg-white border-t p-4 rounded-b-xl">
              <div className="flex justify-end gap-2">
                <button
                  className={`px-4 py-2 text-sm rounded-full text-white font-semibold profiler-bg flex items-center gap-2 ${sendLooksDisabled ? 'opacity-70 cursor-not-allowed' : 'hover:opacity-90'}`}
                  onClick={handleSend}
                  disabled={sendDisabled}
                  title={sendLooksDisabled ? "Select a job to enable sending" : "Send WhatsApp Message"}
                >
                  {sending ? (
                    <>
                      <svg
                        className="animate-spin h-4 w-4"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Sending...
                    </>
                  ) : (
                    <>
                      Send
                      <SendHorizontal size={14} className="inline-block" />
                    </>
                  )}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default WhatsAppModal;
